import { styled } from "@mui/material";
import SearchMatch from "_shared/_views/tables/search-match";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import type { MRT_PaginationState as PaginationState } from "material-react-table";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleTeamRoster from "_shared/fee-schedules/categories/fee-schedule-team-roster";
import type { FeeScheduleTeamMemberDeparture } from "_shared/fee-schedules/categories/_views/fee-schedule-departed-rate";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import useReconcileFeeScheduleStaffing from "_shared/fee-schedules/categories/use-reconcile-fee-schedule-staffing";
import Guid from "_shared/guids/guid";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import React, { useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency, Masked } from "work/masked-fields";
import { useGetFeeScheduleTemplatesQuery } from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import { FeeScheduleTemplateOrderField } from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-field";
import {
  FeeScheduleTemplateOrderParameters,
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import { TemplateTagLine } from "work/fee-schedule-templates/_views/_shared/template-name-with-tags";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import ProposalIssueList from "work/proposals/issues/_views/proposal-issue-list";
import ProposalIssues, { feeScheduleCategoryIssues, ProposalIssue, ProposalIssuePart } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { type TemplateOwnershipScope } from "_shared/_views/templates/template-ownership-filter";
import TemplateSelector, { type TemplateSelectorRow } from "_shared/_views/templates/template-selector";
import useTemplateNameOrder from "_shared/_views/templates/use-template-name-order";
import ResizableTemplatePanel from "work/proposals/_views/_shared/resizable-template-panel";
import useCreateFeeScheduleTemplateAction, {
  type CreatedFeeScheduleTemplate,
} from "work/proposals/_views/_shared/use-create-fee-schedule-template-action";
import {
  NO_TEMPLATE_CONTRIBUTIONS,
  TemplateContributions,
  withTemplateDeselected,
  withTemplateSelected,
} from "work/proposals/_views/_shared/template-contributions";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";
import FeeScheduleRedlineSelection from "work/proposals/redlining/field-redlines/_views/fee-schedule-redline-selection";
import ProposalRedline from "work/proposals/redlining/proposal-redline";
import { withStaffingSeatedOnTeamRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-staffing-redline";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";
import {
  paneIsCompact,
  workPaneNarrowerThan,
} from "work/_views/work-pane-layout";
import { useWorkPaneIsCompact } from "work/_views/use-work-pane-size";

const ListContainer = styled(Grid)(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  flexWrap: "nowrap",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
  width: "100%",
  [paneIsCompact(theme)]: {
    flexDirection: "column",
  },
}));
const FeeScheduleContent = styled(Grid)(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
}));
/** A category's name in the row a template's expander reveals. */
const TemplateRowName = styled(Typography)(() => ({
  flex: "0 1 auto",
  fontSize: "1em",
  fontWeight: 500,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const CategoryRowText = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
}));
const CategoryRowDetail = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  lineHeight: 1.3,
  minWidth: 0,
  overflow: "hidden",
}));
// No top margin at any width. The issue notices above already stand 8px off, and MRT pads its toolbar
// by 8px more; the 16px this added under a 1200px pane doubled the gap between the notice and the
// search. Compact, it was already zeroed for the same reason (#5907).
const RedlineCategorySelection = styled(FeeScheduleRedlineSelection)(({ theme }) => ({
  flex: 1,
  height: "100%",
  minHeight: 0,
  marginLeft: theme.spacing(0),
  minWidth: 0,
  overflow: "hidden",
}));
/** The tab's issues, inset to line up with the table toolbar underneath. */
const TabIssueList = styled(ProposalIssueList)(({ theme }) => ({
  [paneIsCompact(theme)]: {
    paddingInline: theme.spacing(0.5),
  },
  // On a phone the notice lines up with the templates expander above it and the toolbar below (#5907).
  [workPaneNarrowerThan(theme.breakpoints.values.sm)]: {
    paddingInline: 0,
  },
  marginBottom: theme.spacing(1),
  paddingInline: theme.spacing(1),
}));

type FeeScheduleTabProps = {
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  activeReviewField: ProposalField | undefined;
  negotiationId?: string;
  viewerScope?: string;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  isDrawerOpen: boolean;
  onProposalRedlineChange: (newRedline: ProposalRedline, traversalFieldOverride?: ProposalField | null) => void;
  onTraverseToNewField?: (fieldInfo: ProposalField | undefined) => void;
  /**
   * Answers a press on one of the tab's issues (#6097). The page moves to the issue's category, and
   * opens its editor on the Team Member field when the issue is about who bills the rate. Without it,
   * a press only moves to the category, as "Prev / Next Issue" would.
   */
  onIssueSelected?: (issue: ProposalIssue) => void;
  /**
   * Hears the schedule's categories in the order its table draws them, so Prev / Next walks the rows
   * as the reader sorted them (#6617).
   */
  onDisplayedCategoriesChange?: (categoryIds: readonly Guid[] | undefined) => void;
  onCreateCategoryClicked: () => void;
  /** Opens the category editor on a row. Each row carries an Edit action only when this is given. */
  onEditCategoryClicked?: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
  onExistingCategoryClicked: () => void;
  teamMembers: readonly EntityMember[];
  /** The vendor team as the redline has it now, which flags a rate naming somebody off it (#5798). */
  teamRoster?: FeeScheduleTeamRoster;
  /** Who left the vendor team of the work this proposal replaces, whose rates read as departed (#6096). */
  teamMemberDepartures?: readonly FeeScheduleTeamMemberDeparture[];
  /**
   * The templates this viewer's party has ticked, read off the proposal by the page. The tab used to
   * keep them itself, starting from nothing, so a saved selection showed unticked whenever the tab
   * was opened again — it is unmounted whenever another tab is shown — or the page was reloaded.
   */
  selectedTemplateIds: readonly Guid[];
  onFeeScheduleTemplatesUpdated: (templateIds: Guid[], newProposalRedline?: ProposalRedline) => void;
};

const FeeScheduleTab = (props: Readonly<FeeScheduleTabProps>) => {
  const INTERFACE_NAME = "ActiveProposalFeeScheduleTab";

  const {
    proposalRedline,
    activeReviewField,
    negotiationId,
    viewerScope,
    disableEditing,
    fieldLockTooltip,
    isDrawerOpen,
    onProposalRedlineChange,
    onTraverseToNewField,
    onIssueSelected,
    onDisplayedCategoriesChange,
    onCreateCategoryClicked,
    onEditCategoryClicked,
    onExistingCategoryClicked,
    teamMembers,
    teamRoster,
    teamMemberDepartures,
    selectedTemplateIds,
    onFeeScheduleTemplatesUpdated,
  } = props;

  const [templateSearch, setTemplateSearch] = useState("");
  const templateOrder = useTemplateNameOrder();
  const [templatePagination, setTemplatePagination] = useState<PaginationState>(INITIAL_TEMPLATE_PAGINATION);
  const [templateContributions, setTemplateContributions] =
    useState<TemplateContributions>(NO_TEMPLATE_CONTRIBUTIONS);

  const issues: ProposalIssue[] = React.useMemo(() => {
    return (
      props.issues?.entries.filter((issue) =>
        [ProposalField.FeeSchedule, ProposalField.FeeScheduleCategory(issue.field.id ?? Guid.EMPTY)].some((field) =>
          field.isEqualTo(issue.field),
        ),
      ) ?? []
    );
  }, [props.issues?.entries]);
  const teamMemberIssues = React.useMemo(
    () => feeScheduleCategoryIssues(issues, ProposalIssuePart.TeamMember),
    [issues],
  );
  const feeIssues = React.useMemo(() => feeScheduleCategoryIssues(issues, ProposalIssuePart.Fee), [issues]);

  const isMdDownDisplay = useWorkPaneIsCompact();
  const reconcileStaffing = useReconcileFeeScheduleStaffing();
  // An empty roster is not the same as an unreadable one. A masked vendor team cannot be redlined
  // (`withStaffingSeatedOnTeamRedline` returns its input) and offers nobody to hand a rate to.
  const vendorTeamIsMasked = Masked.isMasked(proposalRedline.vendorTeam);
  const session = useSession();
  const userId = session.user!.id;
  const userLegalEntityId = session.user!.legalEntityId;
  const [templateOwnershipScope, setTemplateOwnershipScope] = useState<TemplateOwnershipScope>("all");

  /** A new search means a new first page: page two of the old search is not page two of this one. */
  function handleTemplateSearchChanged(value: string) {
    setTemplateSearch(value);
    setTemplatePagination((current) => ({ ...current, pageIndex: PaginationParameters.DEFAULT_PAGE_INDEX }));
  }

  const templateQueryParams = useMemo(() => {
    const paginationParameters = PaginationParameters.create(templatePagination);
    const filterParameters = new FeeScheduleTemplateFilterParameters({
      entityId: userLegalEntityId,
      creatorUserId: templateOwnershipScope === "own" ? userId : undefined,
      query: templateSearch.trim() || undefined,
    });
    const orderParameters = new FeeScheduleTemplateOrderParameters({
      orderBy: FeeScheduleTemplateOrderField.Name,
      orderDir: templateOrder.orderDirection,
    });
    return new FeeScheduleTemplateQuery({
      filterParameters,
      orderParameters,
      paginationParameters,
    });
  }, [templateOrder.orderDirection, templateOwnershipScope, templatePagination, templateSearch, userId, userLegalEntityId]);

  const templatesQueryArgs = useMemo(
    () => ({ query: templateQueryParams, interfaceName: INTERFACE_NAME }),
    [templateQueryParams],
  );
  const templatesQuery = useGetFeeScheduleTemplatesQuery(templatesQueryArgs);
  const paginatedTemplatesJSON = templatesQuery.data;
  // RTK Query's last answer, kept while a larger request loads or after it fails, so the panel keeps
  // the rows it had and offers the next ones again (review of #6896).
  const templateFeeSchedules = useMemo(
    () => FeeScheduleTemplate.fromJSONArray(paginatedTemplatesJSON?.data ?? []),
    [paginatedTemplatesJSON?.data],
  );

  async function handleTemplateSelectionToggled(template: FeeScheduleTemplate) {
    if (!template.id) throw new Error("No template selected");

    if (Masked.isMasked(proposalRedline.feeSchedule)) {
      throw new Error("Cannot modify fee schedule when it is masked.");
    }

    try {
      let updatedSelectedTemplateIds = [...selectedTemplateIds];
      const isAlreadySelected = selectedTemplateIds.some((id) => id.isEqualTo(template.id));

      let newProposalRedline: ProposalRedline | undefined = undefined;

      if (isAlreadySelected) {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter((id) => !id.isEqualTo(template.id));
        const { contributions, itemKeysToRemove: categoryKeysToRemove } = withTemplateDeselected({
          contributions: templateContributions,
          templateId: template.id,
          alsoSuppliedItemKeys: getCategoryKeysOfLoadedTemplates(updatedSelectedTemplateIds),
        });
        // Read the ids off the redline rather than off the template, so a category the template added
        // and the user has since deleted by hand is not removed a second time.
        const entriesToRemove = proposalRedline.feeSchedule.redlines.flatMap((categoryRedline) =>
          categoryRedline.currentEntry && categoryKeysToRemove.has(getCategoryKey(categoryRedline.currentEntry))
            ? [categoryRedline.currentEntry.id]
            : [],
        );
        if (entriesToRemove.length) {
          let newFeeScheduleRedline: FeeScheduleRedline = proposalRedline.feeSchedule;
          for (const entryId of entriesToRemove) {
            newFeeScheduleRedline = newFeeScheduleRedline.removeEntryByFieldId(entryId);
          }
          newProposalRedline = proposalRedline.updateFeeScheduleRedline(newFeeScheduleRedline);
        }
        setTemplateContributions(contributions);
      } else {
        updatedSelectedTemplateIds.push(template.id);

        // The template's rates say who they are for. Anyone it names who is not on the proposal's
        // vendor team is put to the user first; adding them here is a redline of the team, which
        // the counterparty sees and may reject like any other team change (#5237).
        const staffing = await reconcileStaffing({
          categories: template.categories,
          teamMembers,
          teamIsMasked: vendorTeamIsMasked,
          excludedLegalEntityId: teamRoster?.excludedLegalEntityId,
        });
        if (!staffing.applied) return;
        let newFeeScheduleRedline: FeeScheduleRedline = proposalRedline.feeSchedule;
        const addedCategoryKeys: string[] = [];
        for (const category of staffing.categories) {
          if (getIsCategoryAlreadySelected(category)) continue;
          addedCategoryKeys.push(getCategoryKey(category));
          newFeeScheduleRedline = newFeeScheduleRedline.addEntry(category);
        }
        setTemplateContributions(
          withTemplateSelected({
            contributions: templateContributions,
            templateId: template.id,
            suppliedItemKeys: template.categories.map(getCategoryKey),
            addedItemKeys: addedCategoryKeys,
          }),
        );
        newProposalRedline = proposalRedline.updateFeeScheduleRedline(newFeeScheduleRedline);
        newProposalRedline = withStaffingSeatedOnTeamRedline(
          newProposalRedline,
          staffing.membersToAddToTeam,
        );
      }

      onFeeScheduleTemplatesUpdated(updatedSelectedTemplateIds, newProposalRedline);
    } catch (error) {
      console.error(error);
      showErrorSnackbar(error, { doing: "applying the fee schedule template", operation: "proposal.fee-schedule.apply-template" });
    }
  }

  // No event to stop here any more: the panel's checkbox stops its own press before it reaches the
  // row, so a category is toggled once whether the checkbox or the row around it was pressed.
  async function handleTemplateCategoryToggled(
    templateCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null,
  ) {
    if (Masked.isMasked(proposalRedline.feeSchedule)) throw new Error("Cannot modify fee schedule when it is masked.");

    if (!templateCategory) return;

    if (getIsCategoryAlreadySelected(templateCategory)) {
      const updatedRedline = proposalRedline.updateFeeScheduleRedline(
        proposalRedline.feeSchedule.removeEntryByFieldId(templateCategory.id),
      );

      onProposalRedlineChange(updatedRedline, null);
    } else {
      // One category off a template names a person just as a whole template does.
      const staffing = await reconcileStaffing({
        categories: [templateCategory],
        teamMembers,
        teamIsMasked: vendorTeamIsMasked,
        excludedLegalEntityId: teamRoster?.excludedLegalEntityId,
      });
      if (!staffing.applied) return;
      let updatedRedline = proposalRedline.updateFeeScheduleRedline(
        proposalRedline.feeSchedule.addEntry(staffing.categories[0]),
      );
      updatedRedline = withStaffingSeatedOnTeamRedline(updatedRedline, staffing.membersToAddToTeam);
      onProposalRedlineChange(updatedRedline, null);
    }
  }

  // This tab identifies a category by id everywhere else (`getIsCategoryAlreadySelected`,
  // `removeEntryByFieldId`), so provenance is tracked by id too — keying it by name instead would let
  // a same-name/different-id category be added as a duplicate and then removed in pairs.
  function getCategoryKey(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
    return category.id.value;
  }

  // Supplements the recorded contributions for templates that were already checked when the tab
  // mounted, which have no record of their own. `templateFeeSchedules` is one ownership-filtered
  // page, so this can only ever KEEP a category a still-selected template supplies, never take one
  // away: what it cannot see is covered by the contribution records instead.
  function getCategoryKeysOfLoadedTemplates(templateIds: Guid[]): string[] {
    return templateFeeSchedules
      .filter((loadedTemplate) => templateIds.some((id) => id.isEqualTo(loadedTemplate.id)))
      .flatMap((loadedTemplate) => loadedTemplate.categories.map(getCategoryKey));
  }

  function getIsCategoryAlreadySelected(
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null,
  ): boolean {
    if (Masked.isMasked(proposalRedline.feeSchedule)) {
      throw new Error("Cannot check category when fee schedule is masked.");
    }

    if (!category) return false;
    return proposalRedline.feeSchedule.redlines.some(
      (categoryRedline) => categoryRedline.field.id?.isEqualTo(category.id) && categoryRedline.currentEntry !== null,
    );
  }

  /**
   * The new template arrives already ticked, as it does on the draft, and it was built out of
   * categories that were all there beforehand — it added none of them but it supplies every one.
   *
   * Ticked here because ticking it later would duplicate the fee schedule: the server promotes each
   * category into the catalog under an id of its own, and this tab tells categories apart by id, so
   * every one of them would read as missing and be added again. The ids recorded are projection
   * metadata, not a term either party reviews — see `projectionMetadataPaths`.
   */
  function handleTemplateCreated(createdTemplate: CreatedFeeScheduleTemplate) {
    if (Masked.isMasked(proposalRedline.feeSchedule)) return;
    const updatedSelectedTemplateIds = [...selectedTemplateIds, createdTemplate.id];
    setTemplateContributions(
      withTemplateSelected({
        contributions: templateContributions,
        templateId: createdTemplate.id,
        suppliedItemKeys: proposalRedline.feeSchedule.currentEntries.map(getCategoryKey),
        addedItemKeys: [],
      }),
    );
    onFeeScheduleTemplatesUpdated(updatedSelectedTemplateIds);
  }

  // The fee schedule as it stands once every decision so far is applied, which is what the user is
  // looking at.
  const createTemplate = useCreateFeeScheduleTemplateAction({
    categories: Masked.isMasked(proposalRedline.feeSchedule) ? [] : proposalRedline.feeSchedule.currentEntries,
    proposalName: proposalRedline.name.resultingEntry?.value,
    onTemplateCreated: handleTemplateCreated,
  });

  if (Masked.isMasked(proposalRedline.feeSchedule)) {
    return maskedProposalFieldNotice(MaskedProposalField.FeeSchedule);
  }

  // The rows handed to the selector are the page the server already narrowed to `templateSearch`
  // through `templateQueryParams`'s `Query` — nothing here filters them again.
  const templateRows: TemplateSelectorRow[] = [];
  for (const template of templateFeeSchedules) {
    // A template the page cannot address — nothing to check, nothing to apply — so it is not drawn.
    if (!template.id?.value) continue;

    const templateRowId = template.id.value;
    const isSelected = selectedTemplateIds.some((id) => id.isEqualTo(template.id));
    templateRows.push({
      // The categories the row's expander reveals in place, which is where the list's Collapse put
      // them. By id, like every other category check on this tab.
      children: template.categories.map((category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => ({
        content: (
          <CategoryRowText>
            <TemplateRowName>
              <SearchMatch text={category.name.value} />
            </TemplateRowName>
            <CategoryRowDetail>
              {category.description?.value}
              <br />
              {category instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" : category.feeString}
            </CategoryRowDetail>
          </CategoryRowText>
        ),
        id: `${templateRowId}:${getCategoryKey(category)}`,
        name: category.name.value,
        selected: getIsCategoryAlreadySelected(category),
        onSelectedChanged: () => handleTemplateCategoryToggled(category),
      })),
      detail: <TemplateTagLine tags={template.tags.map((tag) => tag.value)} />,
      id: templateRowId,
      // Some of its categories are already on the redline without the template itself having been
      // applied — the rule the list's own checkbox carried, unchanged.
      indeterminate: !isSelected && template.categories.some((category) => getIsCategoryAlreadySelected(category)),
      inUse: {
        used: template.categories.filter((category) => getIsCategoryAlreadySelected(category)).length,
        total: template.categories.length,
      },
      name: template.name,
      selected: isSelected,
      onSelectedChanged: () => handleTemplateSelectionToggled(template),
    });
  }

  return (
    <ListContainer container direction="row">
      {!disableEditing && (
        <>
          <ResizableTemplatePanel minimized={isDrawerOpen} selectedCount={selectedTemplateIds.length}>
            {/*
              One panel, drawn once. The selector brings its own compact accordion, so the second
              copy this tab used to render behind a media query — and the stale duplicate of every
              control in it — is gone; `compact` is the pane measurement, not the viewport's.

              No `tabs`: there is one list of fee schedule templates in the domain and no second
              source to offer, so the strip stays off rather than being given a single tab to draw.
            */}
            <TemplateSelector
              collapsedLabel="Fee Schedule Templates"
              compact={isMdDownDisplay}
              ownership={{
                label: "fee schedule templates",
                value: templateOwnershipScope,
                onChange: setTemplateOwnershipScope,
              }}
              sorting={templateOrder.sorting}
              pagination={{
                onChange: setTemplatePagination,
                rowCount: paginatedTemplatesJSON?.headers.totalElements ?? 0,
                value: templatePagination,
              }}
              rows={templateRows}
              search={{ onChange: handleTemplateSearchChanged, value: templateSearch }}
              selectedCount={selectedTemplateIds.length}
              status={{
                isError: templatesQuery.isError,
                isFetching: templatesQuery.isFetching,
                isLoading: templatesQuery.isLoading,
                onRetry: () => void templatesQuery.refetch(),
              }}
              subject="fee schedule templates"
            />
          </ResizableTemplatePanel>
          {!isMdDownDisplay && (
            <Grid>
              <Divider orientation="vertical" />
            </Grid>
          )}
        </>
      )}
      <FeeScheduleContent size="grow" sx={{ mt: 1 }}>
        <TabIssueList
          issues={issues}
          activeReviewField={activeReviewField}
          onIssueSelected={onIssueSelected ?? ((issue) => onTraverseToNewField?.(issue.field))}
        />
        <RedlineCategorySelection
          onDisplayedCategoriesChange={onDisplayedCategoriesChange}
          saveAsTemplateAction={disableEditing ? undefined : createTemplate.action}
          activeReviewField={activeReviewField}
          negotiationId={negotiationId}
          viewerScope={viewerScope}
          disableEditing={disableEditing}
          fieldLockTooltip={fieldLockTooltip}
          feeScheduleRedline={proposalRedline.feeSchedule}
          teamRoster={teamRoster}
          teamMemberIssues={teamMemberIssues}
          feeIssues={feeIssues}
          teamMemberDepartures={teamMemberDepartures}
          onCreateCategoryClicked={onCreateCategoryClicked}
          onEditCategoryClicked={onEditCategoryClicked}
          onExistingCategoryClicked={onExistingCategoryClicked}
          onFeeScheduleRedlineChange={(
            newFeeScheduleRedline: FeeScheduleRedline,
            traversalFieldOverride?: ProposalField | null,
          ) => {
            try {
              onProposalRedlineChange(
                proposalRedline.updateFeeScheduleRedline(newFeeScheduleRedline),
                traversalFieldOverride,
              );
            } catch (error) {
              console.error(error);
              showErrorSnackbar(error, { doing: "updating the fee schedule", operation: "proposal.fee-schedule.update" });
            }
          }}
          onCategoryClicked={(categoryId: Guid) => {
            onTraverseToNewField?.(ProposalField.FeeScheduleCategory(categoryId));
          }}
        />
      </FeeScheduleContent>
      {createTemplate.dialog}
    </ListContainer>
  )
};

export default FeeScheduleTab;

/** The page size the tab has always asked the fee-schedule template endpoint for. */
const INITIAL_TEMPLATE_PAGINATION = { pageIndex: 0, pageSize: PaginationParameters.DEFAULT_PAGE_SIZE };
